import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import type { FilterValues } from './PropertyFilters';

const PROPERTY_TYPES = ['villa', 'apartment', 'flat', 'penthouse', 'room', 'commercial', 'land'];
const BEDROOM_OPTIONS = ['1', '2', '3', '4', '5+'];

interface MapFilterBarProps {
  filters: FilterValues;
  onChange: (next: FilterValues) => void;
  resultCount?: number;
}

export function MapFilterBar({ filters, onChange, resultCount }: MapFilterBarProps) {
  const set = (key: keyof FilterValues, value: any) =>
    onChange({ ...filters, [key]: value === 'all' ? undefined : value });

  const amenityCount = (filters.isFurnished ? 1 : 0) + (filters.hasParking ? 1 : 0);

  return (
    <div className="flex items-center gap-2 mb-3 overflow-x-auto scrollbar-hide pb-1">
      {/* Property Type */}
      <Select value={filters.propertyType || 'all'} onValueChange={(v) => set('propertyType', v)}>
        <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0">
          <SelectValue placeholder="Property Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {PROPERTY_TYPES.map((t) => (
            <SelectItem key={t} value={t} className="text-xs capitalize">{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Price */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
            {filters.minPrice || filters.maxPrice
              ? `${filters.minPrice || '0'} - ${filters.maxPrice || '∞'} ETB`
              : 'Price'}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <p className="text-xs font-semibold mb-2">Price Range (ETB)</p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min"
              className="h-9 text-xs"
              value={filters.minPrice || ''}
              onChange={(e) => set('minPrice', e.target.value || undefined)}
            />
            <Input
              type="number"
              placeholder="Max"
              className="h-9 text-xs"
              value={filters.maxPrice || ''}
              onChange={(e) => set('maxPrice', e.target.value || undefined)}
            />
          </div>
          <div className="flex gap-1.5 mt-2">
            {[
              { label: '<1M', min: undefined, max: '1000000' },
              { label: '1-5M', min: '1000000', max: '5000000' },
              { label: '5-10M', min: '5000000', max: '10000000' },
              { label: '10M+', min: '10000000', max: undefined },
            ].map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                className="h-7 text-[10px] flex-1"
                onClick={() => onChange({ ...filters, minPrice: p.min, maxPrice: p.max })}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Bedrooms */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
            {filters.minBedrooms ? `${filters.minBedrooms}+ Beds` : 'Beds'}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-3" align="start">
          <div className="flex gap-1.5">
            <Button
              variant={!filters.minBedrooms ? 'secondary' : 'outline'}
              size="sm"
              className="h-8 text-xs flex-1"
              onClick={() => set('minBedrooms', undefined)}
            >
              Any
            </Button>
            {BEDROOM_OPTIONS.map((n) => (
              <Button
                key={n}
                variant={filters.minBedrooms === n ? 'secondary' : 'outline'}
                size="sm"
                className="h-8 text-xs flex-1"
                onClick={() => set('minBedrooms', n)}
              >
                {n}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Amenities */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Amenities
            {amenityCount > 0 && (
              <Badge className="h-4 w-4 p-0 flex items-center justify-center text-[9px] rounded-full ml-0.5">
                {amenityCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="start">
          <p className="text-xs font-semibold mb-3">Amenities</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Furnished</Label>
              <Switch
                checked={filters.isFurnished || false}
                onCheckedChange={(v) => set('isFurnished', v || undefined)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Parking</Label>
              <Switch
                checked={filters.hasParking || false}
                onCheckedChange={(v) => set('hasParking', v || undefined)}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear */}
      {(filters.propertyType ||
        filters.minPrice ||
        filters.maxPrice ||
        filters.minBedrooms ||
        filters.isFurnished ||
        filters.hasParking) && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-xs shrink-0 text-destructive gap-1"
          onClick={() =>
            onChange({
              ...filters,
              propertyType: undefined,
              minPrice: undefined,
              maxPrice: undefined,
              minBedrooms: undefined,
              isFurnished: undefined,
              hasParking: undefined,
            })
          }
        >
          <X className="h-3 w-3" /> Clear
        </Button>
      )}

      {typeof resultCount === 'number' && (
        <span className="text-xs text-muted-foreground ml-auto shrink-0 pr-1">
          <span className="font-semibold text-foreground">{resultCount}</span> on map
        </span>
      )}
    </div>
  );
}
